// libraries
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

// utils
import { getFocusableElementsOutside } from "/lib/reactutils/utils";
import { UxAngleDown, UxAngleUp } from "../Svg/Angle";

export interface CountyMenuLink {
  label: string;
  href: string;
  target?: "_self" | "_blank";
}

// props
export interface CountyMenuProps {
  /**
   * Name of the current county (e.g. "Nordland")
   */
  currentCountyLabel?: string;

  /**
   * Link for the current county label
   */
  currentCountyHref?: string;

  /**
   * Target for the current county link (e.g. "_self" or "_blank")
   */
  currentCountyTarget?: "_self" | "_blank";

  /**
   * List of counties shown in the county dropdown.
   */
  countyItems: CountyMenuLink[];

  /**
   * Main menu links for the county (e.g. Fylker, Information, Calendar, ...)
   */
  menuItems?: CountyMenuLink[];
}

export default function CountyMenu({
  currentCountyLabel,
  currentCountyHref,
  currentCountyTarget,
  countyItems,
  menuItems,
}: CountyMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCountyDropdownOpen, setIsCountyDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const dropdownInnerRef = useRef<HTMLDivElement | null>(null);

  const resolvedCountyItems = countyItems;
  const resolvedMenuItems = menuItems || [];

  const toggleMobileMenu = () => setIsMenuOpen((prev) => !prev);
  const toggleCountyDropdown = () => setIsCountyDropdownOpen((prev) => !prev);

  // Focus trap inspired by Header: when the menu is open,
  // remove focus from elements outside the county menu.
  useEffect(() => {
    if (!isMenuOpen || !menuRef.current) return;

    const focusContainer = menuRef.current;
    const focusableElementsOutside =
      getFocusableElementsOutside(focusContainer);

    const disableFocusOutside = () => {
      focusableElementsOutside.forEach((el) => {
        el.setAttribute("tabindex", "-1");
      });
    };

    const enableFocusOutside = () => {
      focusableElementsOutside.forEach((el) => {
        el.removeAttribute("tabindex");
      });
    };

    disableFocusOutside();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsCountyDropdownOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      enableFocusOutside();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsCountyDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const currentLabel = currentCountyLabel;

  const primaryMenuItem = resolvedMenuItems[0];
  const secondaryMenuItems = resolvedMenuItems.slice(1);

  // Show all counties in the dropdown, including the current one
  const dropdownCounties = resolvedCountyItems;

  // Desktop: align the left edge of the dropdown content with the left edge of the nav
  useEffect(() => {
    const updateAlignment = () => {
      if (!navRef.current || !dropdownInnerRef.current) return;

      // Use requestAnimationFrame to ensure smooth rendering
      requestAnimationFrame(() => {
        if (!navRef.current || !dropdownInnerRef.current) return;

        const navRect = navRef.current.getBoundingClientRect();
        const innerRect = dropdownInnerRef.current.getBoundingClientRect();

        // Only apply this on desktop widths (match lg breakpoint approximately)
        if (window.innerWidth >= 992) {
          const offset = navRect.left - innerRect.left;
          dropdownInnerRef.current.style.paddingLeft = `${Math.max(
            offset,
            0
          )}px`;
        } else {
          dropdownInnerRef.current.style.paddingLeft = "";
        }
      });
    };

    updateAlignment();
    window.addEventListener("resize", updateAlignment);

    // Recalculate after dropdown opens (account for transitions)
    // Use requestAnimationFrame for better synchronization with browser rendering
    let rafId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (isCountyDropdownOpen) {
      // First frame: initial calculation
      rafId = requestAnimationFrame(updateAlignment);
      // Second frame: after transitions start
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(updateAlignment);
      }, 150);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("resize", updateAlignment);
    };
  }, [isCountyDropdownOpen]);

  return (
    <nav
      className={clsx(
        "county-menu",
        isMenuOpen && "county-menu--menu-open",
        isCountyDropdownOpen && "county-menu--counties-open"
      )}
      aria-label="Fylkesmeny"
      ref={menuRef}
    >
      <div className="county-menu__bar container">
        <div className="county-menu__label-group">
          {currentCountyHref ? (
            <a
              href={currentCountyHref}
              target={currentCountyTarget}
              rel={currentCountyTarget === "_blank" ? "noreferrer" : undefined}
              className="county-menu__label"
            >
              {currentLabel}
            </a>
          ) : (
            <div className="county-menu__label">{currentLabel}</div>
          )}

          <button
            type="button"
            className="county-menu__mobile-toggle"
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            onClick={toggleMobileMenu}
          >
            <span
              aria-hidden="true"
              className="county-menu__mobile-toggle-icon"
            >
              {isMenuOpen ? <UxAngleUp /> : <UxAngleDown />}
            </span>
          </button>
        </div>

        <div className="county-menu__nav" ref={navRef}>
          {primaryMenuItem && (
            <button
              type="button"
              className={clsx(
                "county-menu__nav-link",
                "county-menu__nav-link--toggle",
                isCountyDropdownOpen && "county-menu__nav-link--active"
              )}
              aria-haspopup="true"
              aria-expanded={isCountyDropdownOpen}
              onClick={toggleCountyDropdown}
            >
              {primaryMenuItem.label}
              <span aria-hidden="true" className="county-menu__toggle-icon">
                {isCountyDropdownOpen ? <UxAngleUp /> : <UxAngleDown />}
              </span>
            </button>
          )}

          <div
            className="county-menu__dropdown"
            aria-hidden={!isCountyDropdownOpen}
          >
            <div
              className="county-menu__dropdown-inner container"
              ref={dropdownInnerRef}
            >
              <ul
                className="county-menu__list"
                aria-hidden={!isCountyDropdownOpen}
              >
                {dropdownCounties.map((county, index) => {
                  const tabIndexValue = isCountyDropdownOpen ? 0 : -1;
                  return (
                    <li
                      key={index}
                      className="county-menu__item"
                      aria-hidden={!isCountyDropdownOpen}
                    >
                      <a
                        href={county.href}
                        target={county.target}
                        rel={
                          county.target === "_blank" ? "noreferrer" : undefined
                        }
                        tabIndex={tabIndexValue}
                        suppressHydrationWarning
                        className="county-menu__link"
                      >
                        {county.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {secondaryMenuItems.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target={link.target}
              rel={link.target === "_blank" ? "noreferrer" : undefined}
              className="county-menu__nav-link"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}
